// Inserts section headers. Explicit `group` becomes its own header. Ungrouped
// items that *lead* the list (before the first grouped item) stay first as a
// headerless block in their original order — the omnisearch "feature blocks"
// (saved searches) that sit above the grouped specialty sections, mirroring the
// legacy spotlight. Ungrouped items that appear *after* a group begins bucket by
// first character (`#` for digits). Lists with no `group` stay flat.
const DEFAULT_GROUP = Symbol("ungrouped");

export function groupItems(items, getItemTitle) {
  // Manual headers bypass auto-grouping.
  if (items.some((item) => item !== null && typeof item === "object" && "header" in item)) {
    return items;
  }

  // Nothing is explicitly grouped: leave the list flat, no headers.
  const firstGroupedIndex = items.findIndex((item) => item?.group != null);
  if (firstGroupedIndex === -1) {
    return items;
  }

  // Ungrouped rows ahead of the first grouped row keep their position as a
  // leading, headerless block (e.g. the saved-search feature blocks). Only the
  // rows from the first group onward go through header insertion.
  const leading = items.slice(0, firstGroupedIndex);
  const remainder = items.slice(firstGroupedIndex);

  // A group label is admin-entered, so it reaches these accumulators as an arbitrary string. A
  // null-prototype accumulator keeps `__proto__` and `constructor` ordinary keys, and a symbol
  // sentinel leaves the label `__default__` a group like any other.
  const groups = remainder.reduce((acc, item) => {
    const group = item.group ?? DEFAULT_GROUP;
    acc[group] ??= [];
    acc[group].push(item);
    return acc;
  }, Object.create(null));

  // Mixed explicit + ungrouped: bucket the trailing ungrouped items alphabetically.
  if (DEFAULT_GROUP in groups) {
    const alphabetical = groups[DEFAULT_GROUP].reduce((acc, item) => {
      const firstChar = getItemTitle(item)?.charAt(0).toUpperCase();
      const key = isNaN(firstChar) ? firstChar : "#";
      acc[key] ??= [];
      acc[key].push(item);
      return acc;
    }, Object.create(null));

    delete groups[DEFAULT_GROUP];

    for (const key of Object.keys(alphabetical).sort()) {
      groups[key] = alphabetical[key];
    }
  }

  const grouped = Object.keys(groups)
    .map((group) => [{ header: group }, ...groups[group]])
    .flat();

  return [...leading, ...grouped];
}
